import type { EraDef } from '../types';

/**
 * ERA 3 — AGE OF DINOSAURS
 * Fern jungles, cracked sunbaked ground, dusty sunset light. Boss: T-Rex.
 * Palette: warm jungle greens/ambers with a dusty sunset sun. Enemy bullets: hot
 * red/crimson (contrasts the cyan player bullets). Hazards: fire/lava amber.
 */
const ENEMY_BULLET = 0xff3b3b;
const HAZARD = 0xff9a1f;

export const ERA_DINOSAURS: EraDef = {
  index: 2,
  id: 'dinosaurs',
  name: 'Age of Dinosaurs',
  subtitle: '68 million years ago',
  implemented: true,
  palette: {
    sky: 0x1d2b12,
    fog: 0x3a2c14,
    fogDensity: 0.012,
    ground: 0x3d2b16,
    groundAlt: 0x5a3f1c,
    ambient: 0x6b8f3a,
    sun: 0xffb347,
    sunIntensity: 1.6,
    accent: 0x7dffc0,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'jungle',
    arenaRadius: 68,
    groundStyle: 'cracked',
    props: [
      { visual: { geo: 'cone', color: 0x3f7a2e, emissive: 0x11290c, emissiveIntensity: 0.25, scaleY: 2, anim: 'wobble' }, count: 45, scale: [0.4, 1.0], y: 0 },
      { visual: { geo: 'blob', color: 0x6b5a3f, anim: 'none' }, count: 20, scale: [0.8, 2.2], y: 0 },
      { visual: { geo: 'capsule', color: 0x4a331c, scaleY: 6, anim: 'none' }, count: 15, scale: [0.5, 1.1], y: 1.5 },
    ],
  },
  enemies: [
    {
      id: 'd3_compy',
      name: 'Compsognathus',
      archetype: 'swarmer',
      hp: 12,
      speed: 4,
      damage: 7,
      radius: 0.38,
      xp: 5,
      visual: { geo: 'spike', color: 0x5fbf4a, emissive: 0x1c4a14, emissiveIntensity: 0.5, scale: 0.7, anim: 'bob' },
      // Filler swarmer: just runs at you in packs. Counterplay: cheap kills, but don't let them stack contact hits.
      behavior: { type: 'chase' },
    },
    {
      id: 'd3_raptor',
      name: 'Raptor',
      archetype: 'charger',
      hp: 58,
      speed: 3.6,
      damage: 16,
      radius: 0.6,
      xp: 12,
      visual: { geo: 'cone', color: 0xa8862f, emissive: 0x4a3410, emissiveIntensity: 0.4, scale: 1.1, scaleY: 1.5 },
      // Stalks slower than the player (3.6 < 6 u/s), then winds up and leaps onto where you stood, landing
      // with a small shockwave ring. Counterplay: the wind-up pause is the tell — move before it lands.
      behavior: { type: 'leap', windup: 0.7, range: 9, airTime: 0.55, cooldown: 2.5, landPattern: 'p3_stomp_small' },
    },
    {
      id: 'd3_pterodactyl',
      name: 'Pterodactyl',
      archetype: 'charger',
      hp: 40,
      speed: 3,
      damage: 14,
      radius: 0.55,
      xp: 14,
      visual: { geo: 'octa', color: 0x8f6bb0, emissive: 0x3a2050, emissiveIntensity: 0.5, scale: 1, scaleY: 0.6, anim: 'spin' },
      // Dives from height; engine treats it untargetable while airborne between dives. Counterplay: it's only
      // hittable while hovering/on the ground — dodge the straight dive line, then punish before the next swoop.
      behavior: { type: 'dive', height: 3, cooldown: 3, speedMult: 3.5 },
    },
    {
      id: 'd3_dilophosaurus',
      name: 'Dilophosaurus',
      archetype: 'shooter',
      hp: 37,
      speed: 2.4,
      damage: 12,
      radius: 0.55,
      xp: 12,
      visual: { geo: 'spike', color: 0xd9c23a, emissive: 0x6a5b10, emissiveIntensity: 0.7, scale: 1, anim: 'wobble' },
      // Fire-breather: spits slow blobs that leave a lingering fire pool where they land. Counterplay: keep
      // moving off the cracked-ground pools it leaves behind, don't just dodge the initial spit.
      behavior: { type: 'keepDistance', distance: 9, strafe: 0.3 },
      patterns: ['d3_spit'],
      patternCooldown: 2,
      fireRange: 14,
    },
    {
      id: 'd3_ankylosaurus',
      name: 'Ankylosaurus',
      archetype: 'tank',
      hp: 190,
      speed: 1.4,
      damage: 20,
      radius: 1.2,
      xp: 28,
      shardChance: 0.18,
      visual: { geo: 'blob', color: 0x5a6b4a, emissive: 0x1c2914, emissiveIntensity: 0.4, scale: 2.1, anim: 'none' },
      // Slow tank that drags a short rubble trail while moving and, on a long cooldown, slams a ground
      // shockwave ring outward. Counterplay: don't stand in its wake, and give the slam ring room to pass.
      behavior: { type: 'chase' },
      patterns: ['d3_ankylo_burst'],
      patternCooldown: 5,
      trail: { radius: 0.55, duration: 1.6, dps: 4, interval: 0.5, color: 0x6b5a3f },
    },
    {
      id: 'd3_egg_nest',
      name: 'Egg Nest',
      archetype: 'splitter',
      hp: 25,
      speed: 0,
      damage: 5,
      radius: 0.7,
      xp: 8,
      shardChance: 0.3,
      visual: { geo: 'sphere', color: 0xe8d9a0, emissive: 0x8a6a2a, emissiveIntensity: 0.4, scale: 1.1, anim: 'pulse' },
      // Stationary lobber: arcs heavy, slow-turning homing eggs at you (nothing like era 2's fast needles).
      // Counterplay: the eggs are slow and turn sluggishly — a steady diagonal walk outpaces the turn.
      behavior: { type: 'stationary' },
      patterns: ['d3_egg_lob'],
      patternCooldown: 2.5,
      fireRange: 12,
      onDeath: { spawn: { enemy: 'd3_compy', count: 4, spread: 1.4 } },
    },
    {
      id: 'd3_alpha_raptor',
      name: 'Alpha Raptor',
      archetype: 'elite',
      hp: 100,
      speed: 3.8,
      damage: 22,
      radius: 0.7,
      xp: 30,
      shardChance: 0.4,
      elite: true,
      visual: { geo: 'cone', color: 0xff9a4a, emissive: 0xff9a4a, emissiveIntensity: 0.9, scale: 1.3, scaleY: 1.5, anim: 'spin' },
      // Elite raptor: leaps like its lesser cousin but lands with a bigger double shockwave ring, and still
      // peppers spirals/walls between leaps. Counterplay: same wind-up tell, but give the landing more room.
      behavior: { type: 'leap', windup: 0.55, range: 10, airTime: 0.5, cooldown: 2.2, landPattern: 'p3_stomp_big' },
      patterns: ['d3_raptor_spiral', 'd3_raptor_wall'],
      patternCooldown: 1.4,
      fireRange: 16,
    },
  ],
  patterns: [
    // ---- enemy patterns ----
    {
      id: 'd3_spit',
      emitters: [
        {
          shape: 'aimed',
          interval: 0.9,
          bursts: 3,
          count: 3,
          spread: 0.35,
          bullet: { speed: 9, accel: -3, minSpeed: 2, lifetime: 2.2, radius: 0.24, damage: 11, visual: 'blob', color: HAZARD, linger: { radius: 1.0, duration: 3, dps: 7, color: HAZARD } },
        },
      ],
    },
    {
      // Ground shockwave ring: slow, wide (0.45r), short-lived — era 3's pattern signature. Ankylosaurus's
      // long-cooldown slam.
      id: 'd3_ankylo_burst',
      emitters: [{ shape: 'radial', interval: 1.5, bursts: 1, count: 12, bullet: { speed: 3, lifetime: 1.3, radius: 0.45, damage: 15, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      id: 'd3_raptor_spiral',
      duration: 2.6,
      emitters: [{ shape: 'spiral', interval: 0.09, bursts: 0, count: 2, rotatePerBurst: 0.28, bullet: { speed: 5.5, lifetime: 3.5, radius: 0.22, damage: 12, visual: 'shard', color: ENEMY_BULLET } }],
    },
    {
      id: 'd3_raptor_wall',
      emitters: [{ shape: 'wall', interval: 0.55, bursts: 2, count: 11, spread: 9, gaps: 3, bullet: { speed: 4.2, lifetime: 4, radius: 0.28, damage: 13, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      // Heavy, slow homing egg lob — low turn rate (0.5 rad/s), the opposite of era 2's fast needles.
      id: 'd3_egg_lob',
      emitters: [{ shape: 'aimed', interval: 1.4, bursts: 4, count: 1, spread: 0.5, bullet: { speed: 3.5, lifetime: 4, radius: 0.34, damage: 12, homing: 0.5, homingDelay: 0.6, visual: 'blob', color: HAZARD } }],
    },
    // ---- ground shockwave land patterns (raptor leap, see docs/ERA_RUBRIC.md) ----
    {
      // Small ring shockwave — raptor's leap landing.
      id: 'p3_stomp_small',
      emitters: [{ shape: 'radial', interval: 1, bursts: 1, count: 10, bullet: { speed: 3, lifetime: 1.1, radius: 0.42, damage: 10, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      // Big double ring shockwave — alpha raptor's leap landing, also the T-Rex's phase-3 leap landing.
      id: 'p3_stomp_big',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 14, bullet: { speed: 3.4, lifetime: 1.3, radius: 0.45, damage: 14, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, start: 0.18, bursts: 1, count: 14, bullet: { speed: 2.2, lifetime: 1.6, radius: 0.45, damage: 14, visual: 'ring', color: 0xff7a1f } },
      ],
    },
    // ---- boss patterns ----
    {
      id: 'd3_trex_stomp',
      emitters: [{ shape: 'radial', interval: 0.7, bursts: 3, count: 16, rotatePerBurst: 0.15, bullet: { speed: 4.5, lifetime: 5, radius: 0.3, damage: 16, visual: 'orb', color: ENEMY_BULLET } }],
    },
    {
      // Phase-1 onEnter roar: a single wide ground shockwave ring announcing the fight.
      id: 'p3_trex_roar',
      emitters: [{ shape: 'radial', interval: 1, bursts: 1, count: 16, bullet: { speed: 3.2, lifetime: 1.4, radius: 0.45, damage: 12, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      // Phase 2: sparse, slow homing shots — deliberately thin so the dash special is the actual threat.
      id: 'p3_trex_slow_homing',
      emitters: [{ shape: 'aimed', interval: 2.2, bursts: 3, count: 1, bullet: { speed: 3.2, lifetime: 5, radius: 0.32, damage: 16, homing: 0.4, homingDelay: 0.8, visual: 'blob', color: HAZARD } }],
    },
    {
      // Phase-3 onEnter: a big double shockwave ring marking the final, most dangerous phase.
      id: 'p3_trex_phase3_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 20, bullet: { speed: 3.6, lifetime: 1.6, radius: 0.48, damage: 18, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, start: 0.22, bursts: 1, count: 20, bullet: { speed: 2.2, lifetime: 2, radius: 0.48, damage: 18, visual: 'ring', color: 0xff7a1f } },
      ],
    },
    {
      id: 'd3_trex_homing_fireballs',
      emitters: [
        { shape: 'radial', interval: 0.45, bursts: 4, count: 5, angle: 0.4, bullet: { speed: 3.8, lifetime: 5, radius: 0.32, damage: 18, homing: 0.9, homingDelay: 0.6, visual: 'blob', color: 0xff5c3b } },
      ],
    },
    {
      id: 'd3_trex_meteor',
      emitters: [
        {
          shape: 'random',
          interval: 0.22,
          bursts: 9,
          count: 3,
          spread: 6.283,
          bullet: { speed: 6.5, accel: -3.5, minSpeed: 0.5, lifetime: 2.4, radius: 0.34, damage: 14, visual: 'blob', color: HAZARD, linger: { radius: 1.4, duration: 4.5, dps: 9, color: HAZARD } },
        },
      ],
    },
  ],
  boss: {
    id: 'd3_trex',
    name: 'Tyrannosaurus Rex',
    title: 'Apex of the Cretaceous',
    hp: 3600,
    radius: 3,
    speed: 1.8,
    damage: 30,
    xp: 400,
    visual: { geo: 'spike', color: 0x3a6b2f, emissive: 0xff7a1f, emissiveIntensity: 0.5, scale: 1.7, anim: 'none' },
    phases: [
      {
        // Teaching phase: plain chase + a slow stomp pattern, opened with a roar shockwave onEnter.
        hpBelow: 1,
        name: 'Stalk',
        behavior: { type: 'chase' },
        patterns: ['d3_trex_stomp'],
        patternCooldown: 1.8,
        transition: 1.5,
        onEnter: 'p3_trex_roar',
      },
      {
        // Dash phase: stands still and roars during the windup, then charges across the arena — the actual
        // threat. Bullet patterns stay sparse (slow homing only) so the dash reads clearly.
        hpBelow: 0.6,
        name: 'Roar',
        behavior: { type: 'keepDistance', distance: 11, strafe: 0.3 },
        patterns: ['p3_trex_slow_homing'],
        patternCooldown: 3,
        special: { type: 'dash', interval: 7, windup: 0.9, duration: 0.9, speedMult: 4.5 },
        summons: { enemy: 'd3_compy', count: 4, interval: 7 },
        transition: 2,
        speedMult: 1.2,
      },
      {
        // Extinction phase: faster dash, plus the boss itself leaps and lands with a big shockwave, opened
        // with a big double-ring burst and reinforced by raptor summons.
        hpBelow: 0.3,
        name: 'Extinction',
        behavior: { type: 'leap', windup: 0.9, range: 14, airTime: 0.7, cooldown: 4, landPattern: 'p3_stomp_big' },
        patterns: ['d3_trex_meteor', 'd3_trex_homing_fireballs'],
        patternCooldown: 1,
        special: { type: 'dash', interval: 5, windup: 0.9, duration: 0.9, speedMult: 4.5 },
        summons: { enemy: 'd3_raptor', count: 2, interval: 8 },
        transition: 2,
        speedMult: 1.4,
        onEnter: 'p3_trex_phase3_enter',
      },
    ],
  },
  stages: [
    {
      name: 'Fern Gully',
      duration: 210,
      objective: { type: 'hunt', enemy: 'd3_alpha_raptor', count: 1 },
      density: { start: 1.0, end: 2.6 },
      pool: [
        { enemy: 'd3_compy', weight: 10 },
        { enemy: 'd3_pterodactyl', weight: 4, minTime: 20 },
        { enemy: 'd3_dilophosaurus', weight: 4, minTime: 30 },
        { enemy: 'd3_raptor', weight: 3, minTime: 50 },
        { enemy: 'd3_egg_nest', weight: 2, minTime: 70 },
      ],
      waves: [
        { t: 4, enemy: 'd3_compy', count: 4, formation: 'ring' },
        { t: 25, enemy: 'd3_dilophosaurus', count: 3, formation: 'line' },
        { t: 50, enemy: 'd3_compy', count: 16, formation: 'cluster' },
        { t: 75, enemy: 'd3_raptor', count: 3, formation: 'edge' },
        { t: 100, enemy: 'd3_pterodactyl', count: 4, formation: 'random' },
        { t: 130, enemy: 'd3_egg_nest', count: 2, formation: 'edge' },
        { t: 160, enemy: 'd3_compy', count: 20, formation: 'ring' },
        { t: 185, enemy: 'd3_alpha_raptor', count: 1, formation: 'edge', elite: true },
      ],
    },
    {
      name: 'Sunset Savanna',
      duration: 240,
      density: { start: 1.4, end: 3.2 },
      pool: [
        { enemy: 'd3_compy', weight: 8 },
        { enemy: 'd3_raptor', weight: 5 },
        { enemy: 'd3_dilophosaurus', weight: 5 },
        { enemy: 'd3_pterodactyl', weight: 4 },
        { enemy: 'd3_egg_nest', weight: 2 },
        { enemy: 'd3_ankylosaurus', weight: 2, minTime: 60 },
      ],
      waves: [
        { t: 5, enemy: 'd3_raptor', count: 5, formation: 'ring' },
        { t: 40, enemy: 'd3_ankylosaurus', count: 2, formation: 'line' },
        { t: 75, enemy: 'd3_dilophosaurus', count: 6, formation: 'ring' },
        { t: 105, enemy: 'd3_alpha_raptor', count: 1, formation: 'edge', elite: true },
        { t: 135, enemy: 'd3_compy', count: 30, formation: 'ring' },
        { t: 165, enemy: 'd3_pterodactyl', count: 6, formation: 'random' },
        { t: 195, enemy: 'd3_ankylosaurus', count: 3, formation: 'cluster' },
        { t: 220, enemy: 'd3_alpha_raptor', count: 2, formation: 'line', elite: true },
      ],
    },
    {
      name: 'The Rex\'s Ground',
      duration: 600,
      density: { start: 1.2, end: 1.2 },
      pool: [
        { enemy: 'd3_compy', weight: 6 },
        { enemy: 'd3_raptor', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'd3_compy', count: 10, formation: 'ring' }],
      boss: { id: 'd3_trex', at: 20 },
    },
  ],
  loot: [
    { itemId: 'w_bronze_bow', weight: 1 },
    { itemId: 'a_hide', weight: 1 },
    { itemId: 'r_amber', weight: 1 },
  ],
  audio: { music: 'music/era3_jungle', ambient: 'amb/jungle', sfxSet: 'beast' },
};
